import { gql } from 'graphql-request';
import {
  Nullable,
  ParsedTournament,
  Tournament,
  TournamentStatus,
  TournamentType,
  UserWithToken,
  WithData,
} from '@ygoargentina/sdk/api/api.types';
import { getGraphQLQueryClient } from './query-client';

export type KcgnSnapshotPlayer = {
  cossyId: string;
  name: string;
  rank: number | null;
  points: string | null;
  dropped: boolean | null;
  /**
   * Sólo en el detalle: el usuario nuestro, con el matcheo del import (vínculo
   * del torneo, COSSY, historial); `null` = sin cuenta.
   */
  userId?: number | null;
  /** `KONAMI_COSSY` | `COSSY` | `HISTORY` | `NONE`. */
  matchMethod?: string | null;
};

/** `result` es desde el lado del jugador 1: `WIN` = ganó el 1. */
export type KcgnSnapshotMatch = {
  table: number;
  result: string;
  player1: string;
  player1Id: string;
  player2: string;
  player2Id: string;
  player1Absence: boolean | null;
  player1Abstention: boolean | null;
  player2Absence: boolean | null;
  player2Abstention: boolean | null;
};

export type KcgnSnapshotRound = {
  round: number;
  matches: KcgnSnapshotMatch[];
};

/** Tienda u organizador de YGO Argentina; `logoUrl` es la key de S3. */
export type KcgnSnapshotOrganization = {
  id: number;
  name: string;
  logoUrl: string | null;
  /** La tienda de YGO Argentina a la que corresponde (la propia, en una tienda). */
  storeId: number | null;
};

export type KcgnSnapshotDeckCard = {
  neuronId: number;
  name: string;
  copies: number;
  /** `null` = la carta no está en el catálogo por neuronId. */
  konamiId: number | null;
};

export type KcgnDeckLegality = 'unchecked' | 'legal' | 'illegal';

export type KcgnSnapshotDeck = {
  cossyId: string;
  main: KcgnSnapshotDeckCard[];
  extra: KcgnSnapshotDeckCard[];
  side: KcgnSnapshotDeckCard[];
  deckName: string | null;
  confidence: string | null;
  /** Motivos por los que la lista no es legal. */
  illegal: string[] | null;
  /** `unchecked`: el OTS no la pudo chequear (no es "legal"). */
  legality: KcgnDeckLegality;
  /** Usuario del OTS que la mandó; `null` = sin sesión. */
  senderUserId: number | null;
  /** Cartas sin `konamiId`: con alguna, la lista no se puede importar. */
  unresolved: number;
};

export type KcgnSnapshotRecord = {
  cossyId: string;
  wins: number;
  losses: number;
  draws: number;
};

export type KcgnTournamentSnapshotSummary = {
  id: number;
  tournamentNo: string;
  storeCode: string | null;
  storeName: string | null;
  tournamentName: string | null;
  tournamentDate: string | null;
  status: string | null;
  structure: string | null;
  playerCount: number;
  roundCount: number;
  matchCount: number;
  resultCount: number;
  roundsComplete: boolean;
  lastScreen: string;
  snapshotCount: number;
  updatedAt: string;
  /** El torneo de YGO Argentina con ese `konamiId`; `null` = no está cargado. */
  tournament: {
    id: number;
    title: string;
    status: TournamentStatus;
    organizer: KcgnSnapshotOrganization | null;
  } | null;
  /** La tienda nuestra con ese `konamiStoreCode`; `null` = no está mapeada. */
  store: KcgnSnapshotOrganization | null;
};

/**
 * Lo que "Completar con el snapshot" agregaría al torneo vinculado; todo en
 * cero = no hay nada que completar.
 */
export type KcgnPendingCompletion = {
  /** Jugadores del snapshot sin inscribir (o sin usuario). */
  players: number;
  /** Mesas con resultado en KCGN que acá faltan o no están terminadas. */
  results: number;
  /** Listas completas de inscriptos que no tienen lista. */
  decklists: number;
  /** Inscriptos sin metadeck a los que el detector les asigna uno. */
  metaDecks: number;
  total: number;
};

export type KcgnTournamentSnapshot = KcgnTournamentSnapshotSummary & {
  players: KcgnSnapshotPlayer[];
  rounds: KcgnSnapshotRound[];
  decks: KcgnSnapshotDeck[];
  records: KcgnSnapshotRecord[];
  panelReport: unknown | null;
  /** `null` sin torneo vinculado. */
  pending: KcgnPendingCompletion | null;
};

const SUMMARY_FIELDS = gql`
  fragment KcgnSnapshotSummaryFields on KcgnTournamentSnapshot {
    id
    tournamentNo
    storeCode
    storeName
    tournamentName
    tournamentDate
    status
    structure
    playerCount
    roundCount
    matchCount
    resultCount
    roundsComplete
    lastScreen
    snapshotCount
    updatedAt
    tournament {
      id
      title
      status
      organizer {
        id
        name
        logoUrl
        storeId
      }
    }
    store {
      id
      name
      logoUrl
      storeId
    }
  }
`;

/** La lista, sin roster ni rondas: el detalle se pide aparte. */
export const getKcgnSnapshots = (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithData<KcgnTournamentSnapshotSummary[]>>(gql`
    ${SUMMARY_FIELDS}
    query kcgnTournamentSnapshots {
      data: kcgnTournamentSnapshots {
        ...KcgnSnapshotSummaryFields
      }
    }
  `);

export const getKcgnSnapshot = (
  user: Nullable<UserWithToken>,
  tournamentNo: string
) =>
  getGraphQLQueryClient(user).request<WithData<KcgnTournamentSnapshot | null>>(
    gql`
      ${SUMMARY_FIELDS}
      query kcgnTournamentSnapshot($tournamentNo: String!) {
        data: kcgnTournamentSnapshot(tournamentNo: $tournamentNo) {
          ...KcgnSnapshotSummaryFields
          players {
            cossyId
            name
            rank
            points
            dropped
            userId
            matchMethod
          }
          rounds {
            round
            matches {
              table
              result
              player1
              player1Id
              player2
              player2Id
              player1Absence
              player1Abstention
              player2Absence
              player2Abstention
            }
          }
          decks {
            cossyId
            deckName
            confidence
            illegal
            legality
            senderUserId
            unresolved
            main {
              neuronId
              name
              copies
              konamiId
            }
            extra {
              neuronId
              name
              copies
              konamiId
            }
            side {
              neuronId
              name
              copies
              konamiId
            }
          }
          records {
            cossyId
            wins
            losses
            draws
          }
          panelReport
          pending {
            players
            results
            decklists
            metaDecks
            total
          }
        }
      }
    `,
    { tournamentNo }
  );

/**
 * Importa un torneo de KCGN: sin `tournamentId` crea uno nuevo (organizador =
 * el de la tienda del snapshot), con `tournamentId` vincula ese torneo
 * escribiéndole el `konamiId`. Devuelve el parseo para la pantalla de revisión.
 */
export const importKcgnSnapshot = (
  user: Nullable<UserWithToken>,
  tournamentNo: string,
  options: {
    tournamentId?: number;
    formatId?: number;
    /** Sólo para el torneo nuevo. */
    tournamentType?: TournamentType;
  } = {}
) =>
  getGraphQLQueryClient(user).request<
    WithData<{
      tournament: Tournament;
      importData: Nullable<ParsedTournament>;
    }>
  >(
    gql`
      mutation importKcgnSnapshot(
        $tournamentNo: String!
        $tournamentId: Int
        $formatId: Int
        $tournamentType: TournamentType
      ) {
        data: importKcgnSnapshot(
          tournamentNo: $tournamentNo
          tournamentId: $tournamentId
          formatId: $formatId
          tournamentType: $tournamentType
        ) {
          tournament {
            id
            title
            konamiId
          }
          importData
        }
      }
    `,
    { tournamentNo, ...options }
  );

export type KcgnDecklistImportOutcome =
  | 'saved'
  | 'skipped_unresolved'
  | 'skipped_no_player'
  | 'skipped_existing'
  | 'skipped_locked'
  | 'error';

export type KcgnDecklistImportRow = {
  cossyId: string;
  outcome: KcgnDecklistImportOutcome;
  reason: Nullable<string>;
  decklistId: Nullable<number>;
};

/**
 * Guarda las listas de KCGN del torneo en la inscripción de cada jugador
 * (`decklistId`). Sin `cossyIds` procesa todas; `overwrite` reemplaza la lista
 * que ya tenga la inscripción (nunca una entregada).
 */
export const importKcgnDecklists = (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  options: { cossyIds?: string[]; overwrite?: boolean } = {}
) =>
  getGraphQLQueryClient(user).request<WithData<KcgnDecklistImportRow[]>>(
    gql`
      mutation importKcgnDecklists(
        $tournamentId: Int!
        $cossyIds: [String!]
        $overwrite: Boolean
      ) {
        data: importKcgnDecklists(
          tournamentId: $tournamentId
          cossyIds: $cossyIds
          overwrite: $overwrite
        ) {
          cossyId
          outcome
          reason
          decklistId
        }
      }
    `,
    { tournamentId, ...options }
  );

/** La lista y el metadeck que tiene hoy cada inscripción del torneo. */
export type TournamentPlayerDecklist = {
  id: number;
  decklistId: Nullable<number>;
  metaDeckId: Nullable<number>;
};

export const getTournamentPlayerDecklists = (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<
    WithData<{ players: TournamentPlayerDecklist[] }>
  >(
    gql`
      query tournamentPlayerDecklists($id: Int!) {
        data: tournament(id: $id) {
          players {
            id
            decklistId
            metaDeckId
          }
        }
      }
    `,
    { id: tournamentId }
  );

export type DecklistZoneCard = { code: number; quantity: number };

export type DecklistZones = {
  main: DecklistZoneCard[];
  extra: DecklistZoneCard[];
  side: DecklistZoneCard[];
};

/** Sólo las cartas de una lista (`code` = konamiId), para compararla. */
export const getDecklistZones = (
  user: Nullable<UserWithToken>,
  decklistId: number
) =>
  getGraphQLQueryClient(user).request<
    WithData<{ id: number; decks: Nullable<DecklistZones> }>
  >(
    gql`
      query decklistZones($id: Int!) {
        data: decklist(id: $id) {
          id
          decks {
            main {
              code
              quantity
            }
            extra {
              code
              quantity
            }
            side {
              code
              quantity
            }
          }
        }
      }
    `,
    { id: decklistId }
  );
